import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { GetServicesCarouselDetailResponseModel } from '@models/clinics';
import {
  MedicalServiceGroupPageProps,
  MedicalServicesPageProps,
  SearchServiceResponse,
  ServicesCarouselModel,
  ServicesCarouselSection,
} from '@models/medical-services';

import {
  getMedicalServiceGroupPageData,
  getMedicalServicesCarouselDetails,
  getMedicalServicesPageData,
  getServicesCarousels,
  MEDICAL_SERVICES_CAROUSEL_DETAILS_PAGE_SIZE,
  searchService,
} from './medical-services';
import {
  getCarouselByRecId,
  mapServicesCarouselSection,
  SERVICES_CAROUSELS_QUERY_KEY,
} from './medical-services.helpers';

export const MEDICAL_SERVICES_PAGE_QUERY_KEY = [
  'medicalServicesPageData',
] as const;

export const medicalServiceGroupPageQueryKey = (
  groupId: string,
  recId?: string,
) => ['medicalServiceGroupPageData', recId, groupId] as const;

export const medicalServiceCarouselDetailsQueryKey = (
  carouselId: number,
  search?: string,
) => ['medicalServiceCarouselDetails', carouselId, search ?? ''] as const;

export const useMedicalServiceCarouselDetails = (
  carouselId: number,
  options?: { enabled?: boolean; search?: string },
) => {
  const { enabled = true, search } = options ?? {};
  const trimmedSearch = search?.trim() || undefined;

  return useInfiniteQuery({
    queryKey: medicalServiceCarouselDetailsQueryKey(carouselId, trimmedSearch),
    queryFn: ({ pageParam = 1 }) =>
      getMedicalServicesCarouselDetails(
        carouselId,
        pageParam,
        MEDICAL_SERVICES_CAROUSEL_DETAILS_PAGE_SIZE,
        trimmedSearch,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage: GetServicesCarouselDetailResponseModel) => {
      const loadedCount = lastPage.pageNumber * lastPage.pageSize;

      return loadedCount < lastPage.totalCount
        ? lastPage.pageNumber + 1
        : undefined;
    },
    enabled: !!carouselId && enabled,
  });
};

export const useMedicalServiceCarouselTitle = (
  carouselId: number,
  options?: Omit<
    UseQueryOptions<ServicesCarouselModel[], Error, string>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<string, Error> =>
  useQuery({
    queryKey: SERVICES_CAROUSELS_QUERY_KEY,
    queryFn: getServicesCarousels,
    select: (carousels) =>
      getCarouselByRecId(carousels, carouselId)?.title ?? '',
    enabled: !!carouselId,
    ...options,
  });

export const useMedicalServicesPageData = (
  options?: Omit<
    UseQueryOptions<MedicalServicesPageProps, Error>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<MedicalServicesPageProps, Error> =>
  useQuery({
    queryKey: MEDICAL_SERVICES_PAGE_QUERY_KEY,
    queryFn: getMedicalServicesPageData,
    ...options,
  });

export const useMedicalServiceGroupPageData = (
  groupId: string,
  recId?: string,
  options?: Omit<
    UseQueryOptions<MedicalServiceGroupPageProps, Error>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<MedicalServiceGroupPageProps, Error> =>
  useQuery({
    queryKey: medicalServiceGroupPageQueryKey(groupId, recId),
    queryFn: () => getMedicalServiceGroupPageData(groupId, recId),
    enabled: !!groupId,
    ...options,
  });

export const useSearchService = (
  query: string,
  options?: Omit<UseQueryOptions<SearchServiceResponse, Error>, 'queryKey'>,
): UseQueryResult<SearchServiceResponse, Error> =>
  useQuery<SearchServiceResponse, Error>({
    queryKey: ['searchService', query],
    queryFn: () => searchService(query),
    enabled: !!query,
    ...options,
  });

export const useGetServicesCarousels = (
  options?: Omit<
    UseQueryOptions<ServicesCarouselModel[], Error>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<ServicesCarouselModel[], Error> =>
  useQuery({
    queryKey: SERVICES_CAROUSELS_QUERY_KEY,
    queryFn: getServicesCarousels,
    ...options,
  });

export const useServicesCarouselSection = (
  recId: number,
  options?: Omit<
    UseQueryOptions<ServicesCarouselModel[], Error, ServicesCarouselSection>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<ServicesCarouselSection, Error> =>
  useQuery({
    queryKey: SERVICES_CAROUSELS_QUERY_KEY,
    queryFn: getServicesCarousels,
    select: (carousels) => mapServicesCarouselSection(carousels, recId),
    ...options,
  });
